import React, { useState, useEffect, useRef } from 'react';
// import { appInsights, reactPlugin } from '../../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { jsonEditorStyles } from '../jsonEditorStyles';
import { cloneDeep } from 'lodash';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { SelectChangeEvent } from '@mui/material';
import { Typography } from '@mui/material';
import { SelectComponent } from '../../uiComponents';
import { OptionProps } from '../../../view-models';
import jsonComponentsConfig from '../../../constants/jsonComponents';
import useComponentUIHook from './useComponentUIHook';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

const GenerateConditionalAccessEvaluationUI = ({
  componentData,
  closeGritComponent,
  setUnsavedChanges,
}: componentProps) => {
  const configData = jsonComponentsConfig.conditionalAccessEvaluation.data;
  const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims } = useComponentUIHook(componentData);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const classes = jsonEditorStyles();

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows();
    setDisableSubmit(
      _res ||
        (!(
          modifiedData['idClaim'] &&
          modifiedData['mfaRegisteredClaim'] &&
          modifiedData['changePasswordChallenge'] &&
          modifiedData['mfaChallenge'] &&
          modifiedData['blockChallenge']
      )) ||
      (!sessionFieldsAreValid(modifiedData))
    );
    setUnsavedChanges(_res);
  }, [modifiedData]);

  return (
    <div>
      <FormControl required sx={{ m: 1, minWidth: 600 }}>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim to map to the user object ID to evaluate
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['idClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.idClaim ? modifiedData.idClaim : '',
              label: 'Id Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'string';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim to indicate whether MFA details are registered for the user
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['mfaRegisteredClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.mfaRegisteredClaim ? modifiedData.mfaRegisteredClaim : '',
              label: 'MFA Registered Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'boolean' || allClaims[c]?.dataType === 'string';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
          {allClaims[modifiedData['mfaRegisteredClaim']]?.dataType === 'string' && (
            <Typography variant="body1">
              (MFA will be marked as unregistered if string claim is unset or empty)
            </Typography>
          )}
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Map claims for output flags indicating remediation challenges resulting from the conditional access
          evaluation:
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['changePasswordChallenge']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.changePasswordChallenge ? modifiedData.changePasswordChallenge : '',
              label: 'Must Change Password',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'boolean';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['mfaChallenge']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.mfaChallenge ? modifiedData.mfaChallenge : '',
              label: 'Must Perform MFA',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'boolean';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['blockChallenge']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.blockChallenge ? modifiedData.blockChallenge : '',
              label: 'Must Block User',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'boolean';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        {configData.sessionManagement && (
          <SessionManagement
            modifiedData={modifiedData}
            changeHandler={(data: any) => setModifiedData(data)}
            sessionTypes={configData.sessionTypes}
          />
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            closeGritComponent(cloneDeep(modifiedData));
          }}
          disabled={disableSubmit}
        >
          Save
        </Button>
      </FormControl>
    </div>
  );
};

// export default withAITracking(reactPlugin, GenerateWebAuthnAuthenticationUI);
export default GenerateConditionalAccessEvaluationUI;
